/**
 * Shared UI primitives — inline-styled, no Tailwind dependency.
 */
import React from 'react';

export const Card = ({ children, style = {} }) => (
  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: 20, ...style }}>
    {children}
  </div>
);

export const Button = ({ children, variant = 'primary', size = 'md', disabled, onClick, type = 'button', style = {} }) => {
  const base = { display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 8, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, border: 'none', fontFamily: 'inherit', transition: 'all 0.15s', ...style };
  const sizes = { sm: { padding: '5px 12px', fontSize: 13 }, md: { padding: '8px 16px', fontSize: 14 }, lg: { padding: '10px 20px', fontSize: 15 } };
  const variants = {
    primary: { background: '#1B2A6B', color: '#fff' },
    gold: { background: '#C9A84C', color: '#1B2A6B' },
    outline: { background: 'transparent', color: '#1B2A6B', border: '1.5px solid #1B2A6B' },
    danger: { background: '#ef4444', color: '#fff' },
    ghost: { background: 'transparent', color: '#64748b', border: '1px solid #e2e8f0' }
  };
  return <button type={type} disabled={disabled} onClick={onClick} style={{ ...base, ...sizes[size], ...variants[variant] }}>{children}</button>;
};

export const Badge = ({ children, color = 'gray' }) => {
  const colors = {
    gray: { bg: '#f1f5f9', text: '#64748b' },
    blue: { bg: '#dbeafe', text: '#2563eb' },
    green: { bg: '#dcfce7', text: '#16a34a' },
    red: { bg: '#fee2e2', text: '#dc2626' },
    yellow: { bg: '#fef3c7', text: '#d97706' },
    purple: { bg: '#ede9fe', text: '#7c3aed' },
    navy: { bg: '#e0e7ff', text: '#1B2A6B' },
    gold: { bg: '#fef9c3', text: '#a8882e' }
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: c.bg, color: c.text }}>
      {children}
    </span>
  );
};

export const Input = ({ label, error, ...props }) => (
  <div style={{ marginBottom: 4 }}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>}
    <input {...props} style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${error ? '#ef4444' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', ...props.style }} />
    {error && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{error}</p>}
  </div>
);

export const Select = ({ label, error, children, ...props }) => (
  <div style={{ marginBottom: 4 }}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>}
    <select {...props} style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${error ? '#ef4444' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', cursor: 'pointer', ...props.style }}>
      {children}
    </select>
    {error && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{error}</p>}
  </div>
);

export const Textarea = ({ label, error, ...props }) => (
  <div style={{ marginBottom: 4 }}>
    {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>}
    <textarea {...props} style={{ width: '100%', padding: '8px 12px', border: `1.5px solid ${error ? '#ef4444' : '#e2e8f0'}`, borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', resize: 'vertical', minHeight: 80, ...props.style }} />
    {error && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{error}</p>}
  </div>
);

export const Modal = ({ open, onClose, title, children, width = 540 }) => {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        {title && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #e2e8f0' }}>
            <h3 style={{ fontWeight: 700, fontSize: 16, color: '#1B2A6B' }}>{title}</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1 }}>×</button>
          </div>
        )}
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
};

export const Spinner = ({ size = 24 }) => (
  <div style={{ width: size, height: size, border: `3px solid #e2e8f0`, borderTopColor: '#1B2A6B', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
);

export const PageHeader = ({ title, subtitle, actions }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1B2A6B' }}>{title}</h1>
      {subtitle && <p style={{ color: '#64748b', fontSize: 14, marginTop: 2 }}>{subtitle}</p>}
    </div>
    {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
  </div>
);

export const StageBadge = ({ stage }) => {
  const map = {
    'New': { bg: '#e0e7ff', text: '#4338ca' },
    'Contacted': { bg: '#fef3c7', text: '#d97706' },
    'Interested': { bg: '#d1fae5', text: '#059669' },
    'Proposal Sent': { bg: '#dbeafe', text: '#2563eb' },
    'Negotiation': { bg: '#ede9fe', text: '#7c3aed' },
    'Partnership Closed': { bg: '#dcfce7', text: '#16a34a' },
    'Lost': { bg: '#fee2e2', text: '#dc2626' }
  };
  const c = map[stage] || { bg: '#f1f5f9', text: '#64748b' };
  return <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.bg, color: c.text }}>{stage}</span>;
};

export const PriorityBadge = ({ priority }) => {
  const map = { 'High': '#ef4444', 'Medium': '#f59e0b', 'Low': '#22c55e' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: map[priority] || '#64748b' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: map[priority] || '#94a3b8' }} />
      {priority}
    </span>
  );
};

export const EmptyState = ({ icon = '📭', title, subtitle }) => (
  <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8' }}>
    <div style={{ fontSize: 48, marginBottom: 12 }}>{icon}</div>
    <p style={{ fontWeight: 600, color: '#64748b', fontSize: 16 }}>{title}</p>
    {subtitle && <p style={{ fontSize: 14, marginTop: 4 }}>{subtitle}</p>}
  </div>
);

const pagerBtn = (disabled) => ({
  padding: '6px 14px', borderRadius: 6, border: '1.5px solid #e2e8f0',
  background: disabled ? '#f8fafc' : '#fff',
  color: disabled ? '#cbd5e1' : '#1B2A6B',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: 13, fontFamily: 'inherit', fontWeight: 600,
});

export const Paginator = ({ page, totalPages, onPageChange }) => {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
      <button style={pagerBtn(page === 1)} disabled={page === 1} onClick={() => onPageChange(page - 1)}>← Prev</button>
      <span style={{ fontSize: 13, color: '#64748b' }}>Page {page} of {totalPages}</span>
      <button style={pagerBtn(page === totalPages)} disabled={page === totalPages} onClick={() => onPageChange(page + 1)}>Next →</button>
    </div>
  );
};

export const Table = ({ columns, data, onRowClick }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
          {columns.map((col, i) => (
            <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, ri) => (
          <tr key={ri} onClick={() => onRowClick?.(row)} style={{ borderBottom: '1px solid #f1f5f9', cursor: onRowClick ? 'pointer' : 'default', transition: 'background 0.1s' }}
            onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = '#f8fafc'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            {columns.map((col, ci) => (
              <td key={ci} style={{ padding: '12px 14px', fontSize: 14, color: '#334155', ...col.cellStyle }}>
                {col.render ? col.render(row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
